import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { LobbyKnock } from "../types";

interface Props {
  /** The knock being decided right now (typically the oldest in the queue). */
  knock?: LobbyKnock;
  /** How many more knocks are waiting behind this one — surfaced so the
   *  host knows another prompt is about to appear after Admit/Deny. */
  remaining: number;
  onAdmit: () => void;
  onDeny: () => void;
}

/**
 * Foreground prompt that pops up for the host the moment a guest knocks.
 * Shows one knock at a time — once admitted or denied, the parent removes
 * it from the knock queue and the next one slides in. Sits on top of the
 * meeting (and any encounter page when the host has navigated to PiP).
 */
export default function AdmitKnockModal({
  knock,
  remaining,
  onAdmit,
  onDeny,
}: Props) {
  return (
    <Dialog
      open={!!knock}
      // Escape must not deny — the host has to make an explicit choice via the buttons.
      onClose={(_, reason) => {
        if (reason !== "escapeKeyDown") onDeny();
      }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1 }}>Admit to meeting?</DialogTitle>
      <DialogContent>
        <Stack
          direction="row"
          sx={{
            gap: 2,
            alignItems: "center",
          }}
        >
          <Avatar sx={{ width: 48, height: 48, fontSize: 20 }}>
            {knock?.displayName?.[0]?.toUpperCase() ?? "?"}
          </Avatar>
          <Box>
            <Typography
              sx={{
                fontWeight: 600,
              }}
            >
              {knock?.displayName}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              is asking to join the meeting
            </Typography>
          </Box>
        </Stack>
        {remaining > 0 && (
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              mt: 2,
              display: "block",
            }}
          >
            +{remaining} more {remaining === 1 ? "person" : "people"} waiting
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onDeny} color="error" variant="text">
          Deny
        </Button>
        <Button onClick={onAdmit} variant="contained" autoFocus>
          Admit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
